import Konva from 'konva';
import { KonvaEventObject } from 'konva/lib/Node';
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Group, Line, Rect, Text } from 'react-konva';
import { GraphGroup, GraphStyle, GROUP_SHOW_TYPE } from '../types';
import { groupsHeightForEach, groupsGetHeight } from '../utils';
import Icon, { ICON_TYPE } from './Icon';

/**
 * Da un titolo ad un GROUP
 */
function Header({
	title = "",
	group,
	deep = 0,
	onGroupChange,
	style,
	...props
}: HeaderProps) {

	// HOOKS
	const [mouseOver, setMouseOver] = useState<boolean>(false)

	// settaggio dello zindex
	const refGroup = useRef(null)
	useEffect(() => {
		const node = refGroup?.current
		if (!props.fixed || !node) return
		const length = node?.parent?.children?.length ?? 0
		node.setZIndex(length - 1)
	}, [refGroup?.current?.parent?.children?.length])

	// HENDLERS
	const handleClick = (e: KonvaEventObject<MouseEvent>) => {
		if (group.noCollap || isEmpty) return
		e.cancelBubble = true
		onGroupChange({ ...group, collapsed: !group.collapsed })
	}
	const handleCompactClick = (e: KonvaEventObject<MouseEvent>) => {
		if (group.noCompact) return
		e.cancelBubble = true
		const newShow = group.show == GROUP_SHOW_TYPE.COMPACT ? GROUP_SHOW_TYPE.NORMAL : GROUP_SHOW_TYPE.COMPACT
		groupsHeightForEach([group], null, (g) => {
			g.show = newShow
			//g.collapsed = false
		})
		onGroupChange({ ...group })
	}
	const handleMouseEnter = (e: KonvaEventObject<MouseEvent>) => {
		// style stage container:
		const container = e.target.getStage().container();
		container.style.cursor = "pointer";
		setMouseOver(true)
	}
	const handleMouseLeave = (e: KonvaEventObject<MouseEvent>) => {
		const container = e.target.getStage().container();
		container.style.cursor = "default";
		setMouseOver(false)
	}


	// RENDER
	const textWidth = style.stageMarginLeft
	const isContainer = !!group.groups
	const isEmpty = group.groups?.length == 0
	const isCompact = group.show == GROUP_SHOW_TYPE.COMPACT
	const type: HeaderType = isContainer ? (deep == 0 ? HeaderType.ROOT : HeaderType.CONTAINER) : HeaderType.LEAF

	const color = style.header[type].color
	const font = style.header[type].font
	const yMiddle = (style.header.height / 2) + .5
	const width = style.width - style.stageMarginLeft

	const height = useMemo(()=> groupsGetHeight (group, style), [group, style])
	const offsetX = type == HeaderType.LEAF ? -3 : 0

	return (
		<Group
			{...props}
			onClick={handleClick}
			onTap={handleClick}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			ref={refGroup}
		>

			<Rect
				width={textWidth-1} height={style.header.height}
				x={-textWidth + offsetX} y={0}
				fill={color.bg}
				stroke='black'
				strokeWidth={2}
				strokeEnabled={type == HeaderType.ROOT}
				fillAfterStrokeEnabled={true}
				opacity={isEmpty ? 0.5 : 1}
			/>

			<Text
				text={title}
				fontSize={font.size}
				fontStyle={font.style}
				width={textWidth - style.header.label.margin.right - style.header.label.margin.left}
				wrap="none"
				align={isContainer ? "left" : "right"}
				x={-textWidth + style.header.label.margin.left + offsetX}
				y={font.top}
				fill={color.fg}
			/>

			{!group.noCollap && !isEmpty && <Icon x={0} y={0}
				type={group.collapsed ? ICON_TYPE.RIGHT : (mouseOver ? ICON_TYPE.DOWN : null)}
				color={color.icon}
			/>}

			{(!group.noCompact && !isEmpty && (mouseOver /*|| (isCompact && isContainer)*/)) &&
				<Icon x={-21} y={0}
					type={isCompact ? ICON_TYPE.EXPANDED : ICON_TYPE.COLLAPSE}
					color={color.fg}
					bgColor={color.bg}
					onClick={handleCompactClick}
				/>
			}

			{group.collapsed && !isEmpty && <Line
				points={[20, yMiddle, width, yMiddle]}
				stroke={color.bg/*style.header.collaps.line.color*/}
				dash={[2, 2]}
				strokeWidth={style.header.collaps.line.thickness}
				opacity={0.3}
			/>}

			{!group.collapsed && !isEmpty && type==HeaderType.CONTAINER && <Line
				points={[
					-1.5, 0, 
					-1.5, 0 + height - style.groupMargin
				]}
				stroke={color.bg}
				strokeWidth={1}
				opacity={0.8}
			/>}
		</Group>

	);
}

Header.CalcHeight = (props: HeaderProps) => props.style.header.height

export default Header

type HeaderProps = {
	title?: string,
	group?: GraphGroup,
	deep?: number,
	onGroupChange?: (newGroup: GraphGroup) => void,
	style: GraphStyle,
} & Konva.NodeConfig

enum HeaderType {
	ROOT = "root",
	CONTAINER = "container",
	LEAF = "leaf"
}